import { useEffect, useState } from "react";

/**
 * Floating gold WhatsApp support button.
 * - Sits fixed bottom-right above the safe-area inset (works on PWA / iOS).
 * - Opens https://wa.me/18765028155 in a new tab with a prefilled message.
 * - Hidden inside the splash and on admin print views (those use ?print=1).
 */
const SUPPORT_NUMBER = "18765028155"; // +1 876-502-8155 — SMART customer care
const PREFILL = encodeURIComponent(
    "Hi SMART Support — I need help with my booking. Account email: "
);

export default function WhatsAppButton() {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        // Avoid flash-of-empty before hydration finishes
        const t = setTimeout(() => setMounted(true), 250);
        return () => clearTimeout(t);
    }, []);

    if (!mounted) return null;

    // Suppress on print mode (admin invoice/receipt printout)
    if (typeof window !== "undefined" && window.location.search.includes("print=1")) {
        return null;
    }

    return (
        <a
            href={`https://wa.me/${SUPPORT_NUMBER}?text=${PREFILL}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat with SMART Support on WhatsApp"
            data-testid="whatsapp-support-btn"
            className="
                fixed z-[60]
                bottom-6 right-6
                sm:bottom-7 sm:right-7
                flex items-center gap-2
                pl-3 pr-4 py-3 sm:pl-3.5 sm:pr-5 sm:py-3.5
                rounded-full
                bg-gradient-to-br from-[#FFD86B] via-[#D4AF37] to-[#8E7100]
                text-black font-semibold
                shadow-[0_10px_30px_-8px_rgba(212,175,55,0.65)]
                ring-1 ring-black/10
                hover:shadow-[0_14px_38px_-6px_rgba(212,175,55,0.85)]
                hover:scale-[1.04]
                active:scale-[0.98]
                transition-all duration-300
                whatsapp-pulse
            "
            style={{
                paddingBottom: "max(0.85rem, env(safe-area-inset-bottom))",
            }}
        >
            <span
                className="grid place-items-center w-9 h-9 rounded-full bg-black/90"
                aria-hidden="true"
            >
                {/* Official WhatsApp glyph (CC-BY) — gold on black */}
                <svg
                    viewBox="0 0 32 32"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="text-[#D4AF37]"
                    role="img"
                    aria-hidden="true"
                >
                    <path d="M19.11 17.39c-.28-.14-1.67-.82-1.93-.91-.26-.1-.45-.14-.64.14s-.73.91-.9 1.1c-.16.18-.33.21-.6.07-.28-.14-1.19-.44-2.26-1.39-.84-.74-1.4-1.66-1.57-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.16.18-.28.28-.46.1-.18.05-.34-.02-.48-.07-.14-.64-1.55-.88-2.13-.23-.55-.46-.48-.64-.49-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.26.28-.96.94-.96 2.29s.98 2.66 1.12 2.84c.14.18 1.93 2.94 4.67 4.12.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.67-.68 1.91-1.34.24-.66.24-1.22.16-1.34-.07-.13-.26-.21-.54-.34zM16 4C9.37 4 4 9.37 4 16c0 2.07.53 4.02 1.46 5.72L4 28l6.45-1.43A11.9 11.9 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 4 16 4zm0 21.85a9.83 9.83 0 0 1-5-1.36l-.36-.21-3.83.85.82-3.73-.23-.38A9.83 9.83 0 1 1 25.85 16 9.86 9.86 0 0 1 16 25.85z" />
                </svg>
            </span>
            <span className="text-sm tracking-wide hidden sm:inline">Chat Support</span>
            <span className="text-sm tracking-wide sm:hidden">Help</span>
        </a>
    );
}
